import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  type CurrentUser,
  currentUser,
  enrollTotp,
  login,
  requestPasswordReset,
  verifyTotp,
} from '../api.ts';
import { Announcer } from '../components/Announcer.tsx';
import { LocalePicker } from '../components/LocalePicker.tsx';
import { QrCode } from '../components/QrCode.tsx';
import { ThemePicker } from '../components/ThemePicker.tsx';
import { apiMessage } from '../messages.ts';
import { needsHomeScreen } from '../push.ts';

/**
 * Accesso alla console.
 *
 * Accessibilità (sezione 7bis), perché qui si decide se un volontario cieco
 * può lavorare:
 *  - ogni campo ha una `<label>` vera, non un segnaposto;
 *  - l'errore è collegato al campo con `aria-describedby` e annunciato in una
 *    regione live, altrimenti chi usa uno screen reader non sa che è comparso;
 *  - il focus si sposta sul campo del codice quando compare il secondo
 *    passaggio: senza, si resta a cercarlo con il tab.
 *
 * Nessuna stringa in chiaro: tutto da `useTranslation`.
 */

type Props = {
  onSignedIn: (user: CurrentUser) => void;
};

type Step =
  | { name: 'credentials' }
  | { name: 'totp'; challengeToken: string }
  | { name: 'totp_setup'; challengeToken: string; secret: string; otpauthUri: string }
  | { name: 'forgot' }
  | { name: 'forgot_sent' };

export function LoginPage({ onSignedIn }: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>({ name: 'credentials' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  /** L'indirizzo del modulo "Password dimenticata" (mock 2, `st-forgot`). */
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  const [error, setError] = useState<{ messageKey: string; values?: Record<string, unknown> }>();
  /**
   * Quante volte si è sbagliato.
   *
   * Una regione live annuncia quando il contenuto **cambia**: chi riprova la
   * stessa password e sbaglia allo stesso modo non sentirebbe niente la
   * seconda volta. Il contatore è la stessa soluzione di `Announcer`, ed è qui
   * per la stessa ragione (AM4).
   */
  const [errorSeq, setErrorSeq] = useState(0);
  const [busy, setBusy] = useState(false);

  const codeRef = useRef<HTMLInputElement>(null);
  const forgotEmailRef = useRef<HTMLInputElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (step.name !== 'credentials') {
      // Il secondo passaggio è una schermata nuova: il focus ci entra, così
      // chi naviga da tastiera non deve cercarla.
      headingRef.current?.focus();
      codeRef.current?.focus();
      if (step.name === 'forgot') forgotEmailRef.current?.focus();
    }
  }, [step.name]);

  /**
   * Chiede il collegamento per reimpostare la password.
   *
   * La risposta è identica che l'indirizzo esista o no (G5, mock 2): questo
   * gestore non ha e non deve avere un ramo per "non trovato" — l'unico
   * esito che mostra è quello positivo, sempre.
   */
  async function submitForgot(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (forgotBusy) return;
    setForgotBusy(true);
    setError(undefined);
    try {
      await requestPasswordReset({ email: forgotEmail.trim() });
      setStep({ name: 'forgot_sent' });
    } catch (caught) {
      fail(caught);
    } finally {
      setForgotBusy(false);
    }
  }

  function fail(caught: unknown): void {
    setErrorSeq((previous) => previous + 1);
    if (caught instanceof ApiError) {
      setError({ messageKey: caught.messageKey, values: caught.details });
      return;
    }
    setError({ messageKey: 'errors.unexpected' });
  }

  /**
   * Chi è entrato lo dice il server, non la risposta dell'accesso.
   *
   * La risposta del login non porta l'ente: assemblarne uno qui significava
   * `tenant: null`, e con l'ente spariva il suo fuso orario — la console
   * mostrava gli orari in quello del browser fino al primo ricaricamento
   * (`docs/DECISIONI.md`, E6). Una richiesta in più, una fonte sola.
   */
  async function completeSignIn(user: {
    id: string;
    kind: string;
    displayName: string;
    role: string;
    preferredLocale: string;
  }): Promise<void> {
    try {
      onSignedIn(await currentUser());
    } catch {
      // La sessione c'è comunque: si entra con quel poco che si sa, invece di
      // rimandare indietro chi ha appena inserito la password.
      onSignedIn({
        id: user.id,
        kind: user.kind,
        displayName: user.displayName,
        email,
        role: user.role,
        preferredLocale: user.preferredLocale,
        tenant: null,
        impersonation: false,
      });
    }
  }

  async function submitCredentials(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const result = await login({ email, password });
      if (result.status === 'ok') {
        await completeSignIn(result.user);
        return;
      }
      setStep(
        result.status === 'totp_required'
          ? { name: 'totp', challengeToken: result.challengeToken }
          : {
              name: 'totp_setup',
              challengeToken: result.challengeToken,
              secret: result.secret,
              otpauthUri: result.otpauthUri,
            },
      );
    } catch (caught) {
      fail(caught);
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (step.name !== 'totp' && step.name !== 'totp_setup') return;
    setBusy(true);
    setError(undefined);
    try {
      const result =
        step.name === 'totp'
          ? await verifyTotp({ challengeToken: step.challengeToken, code })
          : await enrollTotp({ challengeToken: step.challengeToken, code, secret: step.secret });

      if (result.status === 'ok') {
        await completeSignIn(result.user);
      }
    } catch (caught) {
      fail(caught);
    } finally {
      setBusy(false);
    }
  }

  const errorId = 'login-error';
  const errorText = error ? apiMessage(t, error.messageKey, error.values ?? {}) : '';

  return (
    <main className="page page--narrow" id="main">
      <div className="card">
        {/*
          Il titolo, e basta.

          Qui accanto stavano lingua e aspetto, cioè due menu a tendina con le
          loro etichette in grassetto **alla stessa altezza della parola
          "Accedi"**: su una schermata che ha due campi, due dei cinque
          controlli erano preferenze, nella posizione più forte della scheda.
          La prima cosa che si vedeva di questo prodotto era un selettore di
          tema.

          Adesso stanno sotto, staccate dalla scheda (`prefs-bar`): chi le
          cerca — chi si è ritrovato la console nella lingua sbagliata — le
          trova al primo sguardo verso il basso, e chi deve solo entrare vede
          il titolo, due campi e un pulsante.
        */}
        <h1 ref={headingRef} tabIndex={-1}>
          {step.name === 'credentials'
            ? t('login.title')
            : step.name === 'forgot'
              ? t('login.forgot_title')
              : step.name === 'forgot_sent'
                ? t('login.forgot_sent_title')
                : t('login.totp_title')}
        </h1>

        {/*
          Due nodi, non uno, ed è la correzione di AS5.

          La **regione** è sempre montata e inizialmente vuota: è la sola forma
          in cui un annuncio arriva. Prima era lo stesso nodo del banner, con
          `hidden={!error}` — e un nodo `hidden` esce dall'albero di
          accessibilità, quindi al primo errore la regione rientrava **già
          piena** e non veniva annunciata. È il difetto che AP1 corregge in
          tutta la console; qui era rimasto perché quel giro questa schermata
          non l'aveva aperta.

          `assertive` perché l'errore blocca il compito in corso, ed è l'unica
          regione assertiva della schermata: due si interrompono a vicenda.
          `seq` fa rileggere lo **stesso** messaggio a chi riprova la stessa
          password e sbaglia di nuovo — senza, la seconda volta sarebbe muta.

          Il **banner** compare solo quando c'è qualcosa da mostrare, e tiene
          l'`id`: è lui il bersaglio di `aria-describedby` dei campi, cioè il
          testo che si sente arrivando sul campo, mentre la regione è quello
          che si sente subito.
        */}
        <Announcer polite="" assertive={errorText} seq={errorSeq} />
        {error ? (
          <p className="alert" id={errorId}>
            {errorText}
          </p>
        ) : null}

        {step.name === 'credentials' ? (
          <form onSubmit={submitCredentials} noValidate>
            <p className="muted">{t('login.subtitle')}</p>

            <div className="field">
              <label htmlFor="email">{t('login.email_label')}</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                aria-describedby={error ? errorId : undefined}
                aria-invalid={error ? true : undefined}
              />
            </div>

            <div className="field">
              <label htmlFor="password">{t('login.password_label')}</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                aria-describedby={error ? errorId : undefined}
                aria-invalid={error ? true : undefined}
              />
            </div>

            <button type="submit" className="button button--primary" disabled={busy}>
              {busy ? t('login.submitting') : t('login.submit')}
            </button>

            {/*
              Sotto il pulsante d'accesso, non accanto ai campi: è la via
              d'uscita da questa schermata, non un terzo campo del modulo
              (mock 2, `st-login`/`st-forgot`).
            */}
            <button
              type="button"
              className="button button--quiet"
              onClick={() => {
                setError(undefined);
                setStep({ name: 'forgot' });
              }}
            >
              {t('login.forgot_link')}
            </button>

            {/*
              L'onboarding delle notifiche comincia qui, non dopo il primo
              turno mancato: su iOS le push arrivano **solo** se la pagina è
              stata aggiunta alla schermata Home, e chi entra da un telefono
              deve saperlo prima (sezione 5.7, mock 2).
            */}
            {needsHomeScreen() ? <p className="muted">{t('login.home_screen_hint')}</p> : null}
          </form>
        ) : step.name === 'totp' || step.name === 'totp_setup' ? (
          <form onSubmit={submitCode} noValidate>
            <p className="muted">{t('login.totp_help')}</p>

            {step.name === 'totp_setup' ? (
              <>
                <QrCode value={step.otpauthUri} label={t('login.totp_setup_qr_label')} />
                <p className="muted">{t('login.totp_setup_or')}</p>
                <p className="secret">
                  <code>{step.secret}</code>
                </p>
              </>
            ) : null}

            <div className="field">
              <label htmlFor="totp">{t('login.totp_label')}</label>
              {/*
                Un solo campo, non sei caselle separate: con uno screen reader
                sei caselle sono un ostacolo (sezione 7bis).
              */}
              <input
                id="totp"
                ref={codeRef}
                name="one-time-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                required
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                aria-describedby={error ? errorId : undefined}
                aria-invalid={error ? true : undefined}
              />
            </div>

            <button type="submit" className="button button--primary" disabled={busy}>
              {busy ? t('login.submitting') : t('login.totp_submit')}
            </button>
          </form>
        ) : step.name === 'forgot' ? (
          <form onSubmit={(event) => void submitForgot(event)} noValidate>
            <p className="muted">{t('login.forgot_hint')}</p>

            <div className="field">
              <label htmlFor="forgot-email">{t('login.email_label')}</label>
              <input
                id="forgot-email"
                ref={forgotEmailRef}
                name="email"
                type="email"
                autoComplete="username"
                required
                value={forgotEmail}
                onChange={(event) => setForgotEmail(event.target.value)}
                aria-describedby={error ? errorId : undefined}
                aria-invalid={error ? true : undefined}
              />
            </div>

            <button type="submit" className="button button--primary" disabled={forgotBusy}>
              {forgotBusy ? t('login.submitting') : t('login.forgot_submit')}
            </button>
            <button
              type="button"
              className="button button--quiet"
              onClick={() => {
                setError(undefined);
                setStep({ name: 'credentials' });
              }}
            >
              {t('login.forgot_back')}
            </button>
          </form>
        ) : (
          <div>
            <p className="muted">{t('login.forgot_sent_hint')}</p>
            {/*
              Stesso "vai all'accesso" delle schermate gemelle
              (`invite.go_to_login`, `password_reset.go_to_login`): qui non si
              sta annullando niente, si è finito. `login.forgot_back` resta
              per il pulsante di annulla del modulo qui sopra, che è
              un'azione diversa anche se porta allo stesso posto.
            */}
            <button
              type="button"
              className="button button--primary"
              onClick={() => {
                setError(undefined);
                setStep({ name: 'credentials' });
              }}
            >
              {t('login.forgot_sent_cta')}
            </button>
          </div>
        )}
      </div>

      {/*
        Lingua e aspetto, fuori dalla scheda e in fondo.

        Non c'è ancora nessun profilo su cui salvarle: qui la scelta vale per
        questo dispositivo, e chiedere al server vorrebbe dire un 401 a ogni
        cambio di lingua. Dopo l'accesso le stesse due scelte stanno nel menu
        del profilo, dove si ricordano anche sul profilo (AS6).
      */}
      <div className="prefs-bar">
        <LocalePicker />
        <ThemePicker />
      </div>
    </main>
  );
}
